import React, { useRef, useState } from 'react';
import { Button, EmptyValue, StatusBadge } from '@/components/ui';
import type { StatusVariant } from '../../../utils/statusUtils';
import { Vehicle } from '../../../types/Vehicle';
import vehicleService from '../../../services/vehicleService';
import { formatDate } from '../../../utils/wrapDate';
import {
  availabilityBadgeVariant,
  fleetStatusBadgeVariant,
  fleetStatusLabel,
  isActiveUnit,
  rateDisplay,
  unitAvailabilityLabel
} from '../utils/equipmentDisplay';
import EquipmentFlags from './EquipmentFlags';

interface EquipmentUnitHeaderProps {
  vehicle: Vehicle;
  canEditPhoto: boolean;
  onUpdated: (vehicle: Vehicle) => void;
}

/**
 * Unit header: photo, unit #, description, status and flags (EQ-15). A unit that isn't Active
 * shows its status only, with no availability pill (EQ-10); an out-of-service unit shows why and
 * when it is expected back (EQ-11).
 */
const EquipmentUnitHeader: React.FC<EquipmentUnitHeaderProps> = ({ vehicle, canEditPhoto, onUpdated }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = isActiveUnit(vehicle);
  const availability = unitAvailabilityLabel(vehicle);
  const outOfService = active && (vehicle.availabilityEffective === 'OutOfService' || vehicle.availability === 'OutOfService');
  const expectedBack = formatDate(vehicle.estimatedReturnToServiceDate);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      onUpdated(await vehicleService.uploadPhoto(vehicle.id, file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload the photo. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start">
      <div className="tw-h-32 tw-w-32 tw-shrink-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-line-input tw-bg-surface-alt">
        {vehicle.imageUrl ? (
          <img src={vehicle.imageUrl} alt={vehicle.unitNumber} className="tw-h-full tw-w-full tw-object-cover" />
        ) : (
          <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-content-muted" aria-hidden="true">
            <i className="bi bi-truck tw-text-4xl" />
          </div>
        )}
      </div>

      <div className="tw-min-w-0 tw-flex-1">
        <h1 className="tw-mb-1 tw-text-2xl tw-font-semibold tw-text-content">{vehicle.unitNumber}</h1>
        <p className="tw-mb-3 tw-text-base tw-text-content-secondary">{vehicle.description || <EmptyValue />}</p>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {active ? (
            <StatusBadge
              status={availability}
              label={availability}
              variant={availabilityBadgeVariant(availability) as StatusVariant}
            />
          ) : (
            <StatusBadge
              status={vehicle.status}
              label={fleetStatusLabel(vehicle.status)}
              variant={fleetStatusBadgeVariant(vehicle.status) as StatusVariant}
            />
          )}
          <EquipmentFlags flags={vehicle.flags} variant="full" />
        </div>
        {outOfService && (vehicle.availabilityReason || expectedBack) && (
          <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-content-secondary">
            {vehicle.availabilityReason && (
              <>
                <span className="tw-font-semibold">Reason:</span> {vehicle.availabilityReason}
              </>
            )}
            {vehicle.availabilityReason && expectedBack && <span aria-hidden="true"> · </span>}
            {expectedBack && (
              <>
                <span className="tw-font-semibold">Expected Back:</span> {expectedBack}
              </>
            )}
          </p>
        )}
        <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-content-muted">
          <i className="bi bi-geo-alt tw-mr-1" aria-hidden="true" />
          {vehicle.foremanArea?.name || 'No foreman area assigned'}
          {vehicle.rate != null && (
            // V3-061: the current rate on the unit, "Rate $55.00 / h" (never Blue Book, V3-046).
            <span className="tw-ml-3 tw-tabular-nums" data-testid="unit-rate">
              <span aria-hidden="true">· </span>Rate {rateDisplay(vehicle)}
            </span>
          )}
        </p>
        {canEditPhoto && (
          <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="tw-hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <Button size="sm" variant="outline-secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? 'Uploading…' : vehicle.imageUrl ? 'Replace Photo' : 'Upload Photo'}
            </Button>
            {vehicle.imageUrl && (
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={uploading}
                onClick={async () => {
                  try {
                    onUpdated(await vehicleService.clearPhoto(vehicle.id));
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Could not remove the photo. Try again.');
                  }
                }}
              >
                Remove Photo
              </Button>
            )}
          </div>
        )}
        {error && <p className="tw-mb-0 tw-mt-2 tw-text-sm tw-text-danger" role="alert">{error}</p>}
      </div>
    </div>
  );
};

export default EquipmentUnitHeader;
